import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DnaIcon } from 'morpheus-ui';
import { Button } from '../../../../../core/components/atoms/Button';
import BrainLoadingWrapper from '../../../../../core/components/molecules/BrainLoading';
import { Typography } from '../../../../../core/components/molecules/Typography';
import { NewContentCard } from '../../../../../core/components/organism/NewContentCard';
import NewPrintComponent from '../../../../../core/components/organism/PrintComponent/newPrintComponent';
import { usePrintComponentStore } from '../../../../../core/components/organism/PrintComponent/store/usePrintComponentStore';
import ExercisePaper from '../../../../../core/components/templates/PrintPaper/exercises';
import { Colors } from '../../../../../core/models/disciplines';
import { getDisciplineIcon, getDisciplineName } from '../../../../../core/utils/discipline';
import { useDoneLesson } from '../../../hooks/DailySchedule/useDoneLesson';
import { useDoneMaterial } from '../../../hooks/DailySchedule/useDoneMaterial';
// Desabilitado junto com a marcação de exercício pelo ícone:
// import { useAnswerExercise } from '../../../hooks/DailySchedule/useAnswerExercise';
// import useDoneDiscurviseExercise from '../../../hooks/DailySchedule/useDoneDiscurviseExercise';
import { getDoneFromPAResponse } from '../../../utils/contentDone';
import {
  IPAContent,
  IPAExerciseDto,
  IPALessonDto,
  IPAMaterialDto,
} from '../../../model/dailySchedule';
import { useDailyScheduleContentStore } from '../../../stores/useDailyScheduleContent';
import {
  handleAllFirstContent,
  orderBySubareaAndAddTitleSection,
  orderByType,
} from '../../../utils/DaySchedule';
import { ContentTypeFilter } from '../../ContentTypeFilter';

type TTitleSection = {
  type: 'titleSection';
  title: string | null;
};

export type ListItemType = TTitleSection | IPAContent;

export function ContentList() {
  const { scheduleId } = useParams();
  const [searchParams] = useSearchParams();

  const materia = searchParams.get('materia');

  const {
    contentList,
    isLoadingContentList,
    selectedContent,
    selectedDiscipline,
    contentListFilter,
    setContentListFilter,
    setSelectedContent,
    setContentList,
  } = useDailyScheduleContentStore((s) => ({
    contentList: s.contentList,
    selectedContent: s.selectedContent,
    isLoadingContentList: s.isLoadingContentList,
    selectedDiscipline: s.selectedDiscipline,
    contentListFilter: s.contentListFilter,
    setContentListFilter: s.setContentListFilter,
    setSelectedContent: s.setSelectedContent,
    setContentList: s.setContentList,
  }));

  const { mutateAsync: onDoneLesson } = useDoneLesson();
  const { mutateAsync: onDoneMaterial } = useDoneMaterial();
  // Desabilitado junto com a marcação de exercício pelo ícone:
  // const { mutateAsync: answerExercise } = useAnswerExercise();
  // const { mutateAsync: markDiscursiveExerciseAsDone } =
  //   useDoneDiscurviseExercise();
  const [isOpenPrint, setIsOpenPrint] = useState(false);
  const [printSectionTitle, setPrintSectionTitle] = useState('');
  const [printDiscipline, setPrintDiscipline] =
    useState<Colors>('interdisciplinar');
  const { setData, setHasApiCall, setHasAnswerSheet } = usePrintComponentStore((s) => ({
    setData: s.setData,
    setHasApiCall: s.setHasApiCall,
    setHasAnswerSheet: s.setHasAnswerSheet,
  }));

  const filteredContentList = useMemo(() => {
    if (!contentList || contentList.length === 0) return [];

    const orderedList = orderBySubareaAndAddTitleSection(contentList);
    const finalArray = orderByType(orderedList);

    if (
      !selectedContent ||
      (selectedContent.content.slug_area &&
        selectedContent.content.slug_area !== selectedDiscipline)
    ) {
      const firstContent = handleAllFirstContent(finalArray);
      if (firstContent) {
        setSelectedContent(firstContent as IPAContent);
      }
    }

    return finalArray;
  }, [contentList]);

  useEffect(() => {
    if (contentListFilter !== 'all') {
      if (
        !selectedContent ||
        selectedContent.type !== contentListFilter ||
        (selectedContent.content.slug_area &&
          selectedContent.content.slug_area !== selectedDiscipline)
      ) {
        handleDefaultContent();
      }
    }
  }, [filteredContentList]);

  function handleSelectContent(content: IPAContent) {
    setSelectedContent(content);
  }

  async function handleDoneContent(
    contentId: string,
    type: 'lesson' | 'exercise' | 'material'
  ) {
    if (!scheduleId) return;

    let response: unknown;

    if (type === 'lesson') {
      response = await onDoneLesson({
        contentId,
        cronogramaId: scheduleId,
      });
    } else if (type === 'material') {
      response = await onDoneMaterial({
        contentId,
        cronogramaId: scheduleId,
      });
    }
    // Marcar exercício como concluído pelo ícone está desabilitado — o
    // exercício conclui ao ser respondido (objetivo) ou concluído na página
    // (discursivo). Mantido comentado para reativar se necessário.
    // else if (type === 'exercise') {
    //   const current = contentList.find((it) => it.content.id === contentId);
    //   const isDiscursive =
    //     (current?.content as IPAExerciseDto)?.type === 'discursive';
    //
    //   if (isDiscursive) {
    //     // Discursivo: rota sem body, apenas marca como concluído.
    //     response = await markDiscursiveExerciseAsDone({
    //       cronogramaId: scheduleId,
    //       exerciseId: contentId,
    //     });
    //   } else {
    //     // Objetivo: sem o aluno resolver, enviamos `respostas` vazio só para
    //     // marcar como concluído (o backend registra como respondido).
    //     response = await answerExercise({
    //       cronogramaId: scheduleId,
    //       exerciseId: contentId,
    //       alternativeId: [],
    //     });
    //   }
    // }

    // Toggle: usa o `done` atual retornado pelo endpoint como fonte da verdade.
    const done = getDoneFromPAResponse(response);

    const newContentList = contentList.map((item) => {
      if (item.content.id === contentId) {
        return { ...item, content: { ...item.content, done } };
      }

      return item;
    });

    setContentList(newContentList);
  }

  function handleDefaultContent() {
    if (!contentList || contentList.length === 0) {
      setSelectedContent(null);
      setContentListFilter('lesson');

      return;
    }

    const sortedList = contentList?.sort((a, b) => {
      if (!('content' in a) || !('content' in b)) return 0;

      if ('subareaOrder' in a.content && 'subareaOrder' in b.content) {
        if (a.content.subareaOrder !== b.content.subareaOrder) {
          return a.content.subareaOrder - b.content.subareaOrder;
        }
      }

      if ('order' in a.content && 'order' in b.content) {
        return a.content.order - b.content.order;
      }

      return 0;
    });

    if (materia === 'redacao') {
      const contents = sortedList.filter((item) => item.type === 'essay');
      const contentTypes = sortedList.filter(
        (item) => item.type === 'lesson' || item.type === 'essay'
      );

      if (contentTypes.length === 1 && contentTypes[0].type === 'essay') {
        setContentListFilter('essay');
        setSelectedContent(contentTypes[0]);

        return;
      }

      if (contents.length > 0 && contentListFilter === 'essay') {
        const firstContent = contents.find((item) => !item.content.done);

        if (firstContent) {
          setSelectedContent(firstContent);
          setContentListFilter('essay');

          return;
        }
      }
    }

    const contents = sortedList.filter(
      (item) => item.type === contentListFilter
    );

    if (contents.length > 0) {
      const firstContent = contents.find((item) => !item.content.done);

      if (firstContent) {
        setSelectedContent(firstContent);
      }
    }
  }

  function handleOpenPrint(
    sectionTitle: string,
    exercisesInSection: IPAExerciseDto[]
  ) {
    if (!exercisesInSection.length) return;

    setHasApiCall(false);
    setHasAnswerSheet(true);
    setData(exercisesInSection);
    setPrintSectionTitle(sectionTitle || 'Exercícios');
    setPrintDiscipline(
      (exercisesInSection[0]?.slug_area ||
        selectedDiscipline ||
        'interdisciplinar') as Colors
    );
    setIsOpenPrint(true);
  }

  const TitleSection = ({ title }: { title: string | null | undefined }) => {
    return title === undefined || title === null ? (
      <></>
    ) : (
      <Typography
        size='h6'
        type='titleSection'
        className='uppercase text-mono-color-dark '
      >
        {title}
      </Typography>
    );
  };

  useEffect(() => {
    if (typeof window?.MathJax !== 'undefined') {
      window.MathJax.typesetPromise();
    }
  });

  // Função auxiliar para ordenar conteúdo por tipo
  const sortContentByType = (a: IPAContent, b: IPAContent): number => {
    if (!a?.type || !b?.type) return 0;

    const typeOrder: Record<string, number> = {
      lesson: 0,
      exercise: 1,
      material: 2,
    };

    const aOrder = typeOrder[a.type] ?? Number.MAX_SAFE_INTEGER;
    const bOrder = typeOrder[b.type] ?? Number.MAX_SAFE_INTEGER;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    if (a.type === 'material' && b.type === 'material') {
      const categoryOrder: Record<string, number> = {
        apostila: 0,
        mapa_mental: 1,
      };

      const aCategory = (
        (a.content as IPAMaterialDto).fileCategory || ''
      ).toLowerCase();
      const bCategory = (
        (b.content as IPAMaterialDto).fileCategory || ''
      ).toLowerCase();

      const aCategoryOrder =
        categoryOrder[aCategory] ?? Number.MAX_SAFE_INTEGER;
      const bCategoryOrder =
        categoryOrder[bCategory] ?? Number.MAX_SAFE_INTEGER;

      if (aCategoryOrder !== bCategoryOrder) {
        return aCategoryOrder - bCategoryOrder;
      }
    }

    return 0;
  };

  const groupedContentBySections = useMemo(() => {
    const normalizeSectionTitle = (title: string | null | undefined) =>
      (title || '').trim();

    const getOrCreateSectionIndex = (
      sections: Array<{ title: string; content: IPAContent[] }>,
      sectionIndexes: Map<string, number>,
      title: string | null | undefined
    ) => {
      const normalizedTitle = normalizeSectionTitle(title);
      const existingIndex = sectionIndexes.get(normalizedTitle);

      if (existingIndex !== undefined) {
        return existingIndex;
      }

      const newIndex = sections.length;
      sections.push({
        title: normalizedTitle,
        content: [],
      });
      sectionIndexes.set(normalizedTitle, newIndex);

      return newIndex;
    };

    const sections: Array<{
      title: string;
      content: IPAContent[];
    }> = [];
    const sectionIndexes = new Map<string, number>();
    let currentSectionIndex = -1;

    filteredContentList.forEach((item) => {
      // Se é um título de seção, cria nova seção
      if (item.type === 'titleSection') {
        const normalizedTitle = normalizeSectionTitle(item.title);

        // Quando o título é nulo/vazio, mantém o agrupamento na seção anterior
        // e só cria seção padrão se ainda não existir nenhuma seção.
        if (!normalizedTitle) {
          if (currentSectionIndex === -1) {
            currentSectionIndex = getOrCreateSectionIndex(
              sections,
              sectionIndexes,
              'Não definido'
            );
          }
          return;
        }

        currentSectionIndex = getOrCreateSectionIndex(
          sections,
          sectionIndexes,
          normalizedTitle
        );
        return;
      }

      // Se não há seção atual, cria a seção padrão
      if (currentSectionIndex === -1) {
        currentSectionIndex = getOrCreateSectionIndex(
          sections,
          sectionIndexes,
          null
        );
      }

      // Adiciona conteúdo à seção atual
      sections[currentSectionIndex].content.push(item);
    });

    // Remove seções vazias
    return sections.filter((section) => section.content.length > 0);
  }, [filteredContentList]);

  const filteredSections = useMemo(() => {
    const normalizeSectionTitle = (title: string | null | undefined) =>
      (title || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

    const isUndefinedSection = (title: string) => {
      const normalizedTitle = normalizeSectionTitle(title);
      return normalizedTitle === '' || normalizedTitle.includes('nao definido');
    };

    const getSectionTypeOrder = (content: IPAContent[]) => {
      if (content.some((item) => item.type === 'lesson')) return 0;
      if (content.some((item) => item.type === 'exercise')) return 1;
      if (content.some((item) => item.type === 'material')) return 2;
      return Number.MAX_SAFE_INTEGER;
    };

    const sections = groupedContentBySections.reduce<
      Array<{ title: string; content: IPAContent[]; originalIndex: number }>
    >((acc, section, originalIndex) => {
      const content =
        contentListFilter === 'all'
          ? [...section.content].sort(sortContentByType)
          : section.content.filter((item) => item.type === contentListFilter);

      if (content.length === 0) return acc;

      acc.push({
        ...section,
        content,
        originalIndex,
      });
      return acc;
    }, []);
    sections.sort((a, b) => {
      const undefinedDiff =
        Number(isUndefinedSection(a.title)) - Number(isUndefinedSection(b.title));
      if (undefinedDiff !== 0) return undefinedDiff;
      const typeDiff = getSectionTypeOrder(a.content) - getSectionTypeOrder(b.content);
      if (typeDiff !== 0) return typeDiff;

      return a.originalIndex - b.originalIndex;
    });

    return sections.map(({ originalIndex, ...section }) => section);
  }, [groupedContentBySections, contentListFilter]);

  return (
    <div className='flex flex-col gap-lg'>
      <NewPrintComponent
        type='exerciseCronogram'
        isOpen={isOpenPrint}
        onClose={() => {
          setIsOpenPrint(false);
          setHasApiCall(false);
          setHasAnswerSheet(false);
          setData([]);
        }}
        onCancel={() => {
          setIsOpenPrint(false);
          setHasApiCall(false);
          setHasAnswerSheet(false);
          setData([]);
        }}
        cancelLabel='Cancelar'
        showCancelIcon={false}
        title={printSectionTitle}
        subtitle={getDisciplineName(printDiscipline || 'interdisciplinar')}
      >
        <ExercisePaper
          cols='split'
          size='default'
          color={printDiscipline ?? 'interdisciplinar'}
          replaceStorage={true}
          iconType='svg'
          icon={
            getDisciplineIcon(printDiscipline || 'interdisciplinar') || DnaIcon
          }
          title={printSectionTitle}
          module={getDisciplineName(printDiscipline || 'interdisciplinar')}
        />
      </NewPrintComponent>
      <ContentTypeFilter
        value={contentListFilter}
        onChange={setContentListFilter}
      />
      {isLoadingContentList ? (
        <BrainLoadingWrapper customMessage='Carregando conteúdos...' />
      ) : (
        <div className='flex flex-col gap-lg'>
          {filteredSections.map((section, idx) => {
            if (!section) return null;
            const exercisesInSection = section.content
              .filter((item) => item.type === 'exercise')
              .map((item) => item.content as IPAExerciseDto);
            return (
              <div
                key={`${section.title}-${contentListFilter}-${idx}`}
                className='flex flex-col gap-lg'
              >
                <div className='flex items-center justify-between gap-sm'>
                  <TitleSection title={section.title} />
                  {exercisesInSection.length > 0 && (
                    <Button
                      LeftIcon='print'
                      size='small'
                      className='py-1 px-5 mr-2'
                      onClick={() =>
                        handleOpenPrint(section.title, exercisesInSection)
                      }
                    >
                      <Typography
                        size='default'
                        className='text-white text-sm'
                      >
                        Imprimir
                      </Typography>
                    </Button>
                  )}
                </div>
                {section.content.map((item) => {
                  return (
                    <NewContentCard.Root
                      key={item.content.id}
                      contentType={item.type}
                      contentId={item.content.id}
                      contentSlug={item.content?.slug}
                      subject={item.content?.subject?.title || ''}
                      discipline={item.content.slug_area || selectedDiscipline}
                      selected={item.content.id === selectedContent?.content.id}
                      onClick={() => handleSelectContent(item)}
                    >
                      <NewContentCard.Header
                        done={item.content.done}
                        firstViewedAt={item.content.firstViewedAt}
                        showSubject={[
                          'material',
                          'exercise',
                          'lesson',
                          'essay',
                        ].includes(item.type)}
                        onClickDone={(contentId) =>
                          handleDoneContent(
                            contentId,
                            item.type as 'lesson' | 'material' | 'exercise'
                          )
                        }
                        // Exercício e redação não podem ser marcados como
                        // concluídos pelo ícone: exercício conclui ao responder
                        // e redação ao enviar.
                        canClickDone={
                          item.type !== 'exercise' && item.type !== 'essay'
                        }
                      />
                      <NewContentCard.Title
                        title={
                          item.type === 'exercise'
                            ? (item.content as IPAExerciseDto).stripedStatement
                            : item.content.title
                        }
                        isHtml={item.type === 'exercise'}
                      />
                      <NewContentCard.Footer
                        isFavorite={item.content.favorite}
                        hideFavoriteButton={item.type === 'essay'}
                        teacher={
                          item.type === 'lesson'
                            ? (item.content as IPALessonDto).teacher[0]?.name
                            : undefined
                        }
                        lessonType={
                          item.type === 'lesson' &&
                          (item.content as IPALessonDto).type === 'summary'
                            ? 'Revisão'
                            : undefined
                        }
                        duration={
                          item.type === 'lesson'
                            ? (item.content as IPALessonDto).duration
                            : undefined
                        }
                        materialType={
                          item.type === 'material'
                            ? (item.content as IPAMaterialDto).fileCategory
                            : undefined
                        }
                        incidence={
                          item.type === 'lesson'
                            ? (item.content as IPALessonDto).incidence
                            : undefined
                        }
                        // correctAnswersPercentage={
                        //   item.type === 'exercise'
                        //     ? (item.content as IPAExerciseDto)
                        //         .correctAnswersPercentage
                        //     : undefined
                        // }
                      />
                    </NewContentCard.Root>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
